import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { supabase } from '../../lib/supabase';

/**
 * Shown when a user follows a password-reset link from their email.
 * Supabase redirects to /reset-password with access_token in the URL hash;
 * `detectSessionInUrl: true` in the client picks it up automatically and fires
 * a PASSWORD_RECOVERY auth event — at that point `session` is non-null.
 */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const updatePassword = useAuthStore((s) => s.updatePassword);

  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let settled = false;

    // Supabase processes the recovery token via detectSessionInUrl during client
    // init, before any component mounts.  PASSWORD_RECOVERY fires then — before
    // our listener exists.  Check the current session as a fallback.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!settled && session) {
        settled = true;
        setReady(true);
      }
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        settled = true;
        setReady(true);
      }
    });

    // If neither fires within 8s the link is invalid or expired.
    const timeout = setTimeout(() => {
      if (!settled) setError('This reset link is invalid or has expired. Please request a new one.');
    }, 8000);

    return () => {
      listener.subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      await updatePassword(password);
      navigate('/login', { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  }

  if (!ready) {
    return (
      <>
        <h2 className="text-xl font-semibold text-gray-100 mb-2 text-center">
          {error ? 'Link invalid' : 'Verifying reset link…'}
        </h2>
        {error ? (
          <>
            <p className="text-sm text-loss text-center mb-4">{error}</p>
            <a
              href="/forgot-password"
              className="btn-primary w-full flex items-center justify-center"
            >
              Request a new link
            </a>
          </>
        ) : (
          <p className="text-sm text-gray-500 text-center">
            Please wait while we validate your password-reset link.
          </p>
        )}
      </>
    );
  }

  return (
    <>
      <h2 className="text-xl font-semibold text-gray-100 mb-1">Set new password</h2>
      <p className="text-sm text-gray-500 mb-6">Choose a strong password for your account.</p>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-loss/10 border border-loss/25 text-sm text-loss">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5" htmlFor="password">
            New password
          </label>
          <input
            id="password"
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
            placeholder="At least 8 characters"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5" htmlFor="confirm">
            Confirm new password
          </label>
          <input
            id="confirm"
            type="password"
            required
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
            placeholder="Re-enter new password"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <KeyRound className="w-4 h-4" />
          {loading ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </>
  );
}
